<script setup>
import { computed, ref } from 'vue';
import { VTextField } from 'vuetify/components/VTextField';

const props = defineProps({
    modelValue: {},
    label: {},
    errorMessages: {},
    mode: {
        type: String,
        default: 'edit'
    },
    align: {},
    disabled: {},
    formatOptions: {
        type: Object,
        default: () => ({
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true
        })
    }
});

const emit = defineEmits(['update:modelValue']);
const isEditing = ref(false);

// Get the user's locale from the browser
const userLocale = navigator.language || navigator.userLanguage || 'en';

const formattedValue = computed({
    get: () => {
        if (isEditing.value) {
            return props.modelValue?.toString() || '';
        }
        if (!props.modelValue && props.modelValue !== 0) return '';
        const num = parseFloat(props.modelValue);
        return isNaN(num)
            ? ''
            : num.toLocaleString(userLocale, props.formatOptions);
    },
    set: (value) => {
        // Remove any commas or dots based on locale format
        const cleanValue = value.replace(/[,.\s]/g, '.');

        // Handle special case for decimal point
        if (cleanValue === '.') {
            emit('update:modelValue', '0.');
            return;
        }

        // Check if it's a valid number or empty
        if (cleanValue === '' || cleanValue === '-') {
            emit('update:modelValue', cleanValue);
            return;
        }

        const num = parseFloat(cleanValue);
        if (!isNaN(num)) {
            emit('update:modelValue', cleanValue);
        }
    }
});

const onFocus = () => {
    isEditing.value = true;
};

const onBlur = () => {
    isEditing.value = false;
    if (props.modelValue) {
        const num = parseFloat(props.modelValue);
        if (!isNaN(num)) {
            emit('update:modelValue', num.toString());
        }
    }
};
</script>

<template>
    <VTextField
        v-model="formattedValue"
        density="compact"
        hide-details="true"
        :error-messages="errorMessages"
        :variant="mode === 'edit' ? 'outlined' : 'filled'"
        :readonly="mode === 'edit' ? false : true"
        color="primary"
        :class="['number-field', align ? `text-${align}` : '']"
        :disabled="disabled"
        :clearable="false"
        append-icon=""
        clear-icon=""
        :persistent-clear="false"
        @focus="onFocus"
        @blur="onBlur"
    />
</template>

<style>
.number-field {
    width: 100%;
}

.number-field input {
    padding: 0 !important;
    text-align: right;
}

.number-field .v-field__append-inner,
.number-field .v-field__append,
.number-field .v-input__append,
.number-field .v-field__clearable {
    display: none !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 0 !important;
}

.number-field .v-field__field {
    padding-right: 0 !important;
}

.number-field .v-field__input {
    padding-right: 12px !important;
    min-height: var(--v-input-control-height) !important;
}

.number-field .v-field {
    padding-right: 0 !important;
}
</style>
